import { cssVar } from 'src/app/styles/helper';
import { TypographyPropKeys } from 'src/app/styles/typography';
import styled, { css } from 'styled-components';
import { HEADING_STYLES } from './Heading.styles';
import { PARAGRAPH_STYLES } from './Paragraph.styles';
import { ColorModePropKeys } from 'src/app/styles/colors';

export type TypographyType = 'paragraph' | 'heading';
export type TypographySize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const getTypographyStyles = (type: TypographyType, size: TypographySize) => {
  if (type === 'heading') {
    return HEADING_STYLES[size];
  } else {
    // paragraph type
    if (size === '2xl') {
      throw new Error('Size "2xl" is not supported for paragraph type. Please use a heading type instead.');
    }

    return PARAGRAPH_STYLES[size];
  }
};

const lineClampStyles = css<{ $lineClamp?: number }>`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ $lineClamp }) => $lineClamp};
  overflow: hidden;
`;

export interface TypographyComponentProps {
  type: TypographyType;
  size: TypographySize;
  fontSize?: TypographyPropKeys;
  lineHeight?: TypographyPropKeys;
  fontWeight?: TypographyPropKeys;
  color?: ColorModePropKeys;
  textAlign?: 'left' | 'center' | 'right';
  textTransform?: 'uppercase' | 'lowercase' | 'capitalize';
  letterSpacing?: number;
  whiteSpace?: 'normal' | 'nowrap' | 'pre' | 'pre-wrap' | 'pre-line' | 'break-spaces';
  display?: 'inline' | 'block';
  lineClamp?: number;
}

type TypographyContainerProps = {
  [K in keyof TypographyComponentProps as `$${K}`]: TypographyComponentProps[K];
};

export const TypographyContainer = styled.div<TypographyContainerProps>`
  margin: 0;
  padding: 0;
  font-family: ${({ $type }) => cssVar($type === 'heading' ? 'font-family-display' : 'font-family-body')};
  ${({ $display }) => $display && `display: ${$display};`};

  ${({ $lineClamp }) => $lineClamp && lineClampStyles}

  ${({ $color }) =>
    $color &&
    css`
      color: ${cssVar($color)};
    `}
  ${({ $textAlign }) =>
    $textAlign &&
    css`
      text-align: ${$textAlign};
    `}
  ${({ $textTransform }) =>
    $textTransform &&
    css`
      text-transform: ${$textTransform};
    `}
  ${({ $letterSpacing }) =>
    $letterSpacing &&
    css`
      letter-spacing: ${$letterSpacing}px;
    `}
  ${({ $whiteSpace }) =>
    $whiteSpace &&
    css`
      white-space: ${$whiteSpace};
    `}


  ${({ $type, $size, $fontSize, $lineHeight, $fontWeight }) => {
    let styles = getTypographyStyles($type, $size);

    // Override with custom props if provided
    if ($fontSize) {
      styles = css`
        ${styles}
        font-size: ${cssVar($fontSize)};
      `;
    }
    if ($lineHeight) {
      styles = css`
        ${styles}
        line-height: ${cssVar($lineHeight)};
      `;
    }
    if ($fontWeight) {
      styles = css`
        ${styles}
        font-weight: ${cssVar($fontWeight)};
      `;
    }

    return styles;
  }}
`;
